import React, { useMemo } from 'react';
import moment from 'moment';
import ModalSemantic from 'components/modalSemantic';
import { Button } from 'semantic-ui-react';
import {
  IProviderCaseloadClient,
  IProviderCaseloadSession
} from 'model/calendar/events';
import { CaseloadContent } from './CaseloadModal.style';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  providerName: string;
  caseload: IProviderCaseloadClient[];
}

const DOB_INPUT_FORMAT = 'YYYY-MM-DD';
const DOB_DISPLAY_FORMAT = 'MM/DD/YYYY';

const formatDob = (dob?: string) => {
  if (!dob) return '—';
  const parsed = moment(dob, DOB_INPUT_FORMAT);
  return parsed.isValid() ? parsed.format(DOB_DISPLAY_FORMAT) : '—';
};

/**
 * Collapses a client's sessions into "Mon 9:00 AM, 1:00 PM; Wed 10:30 AM" —
 * grouped by day, so a client seen twice on one day reads as one entry.
 *
 * A series that has not begun yet carries its start date on the specific time
 * it belongs to, not on the row, so a client with one running and one upcoming
 * session cannot be misread as starting later altogether.
 */
const formatSessions = (sessions: IProviderCaseloadSession[]) => {
  const label = (session: IProviderCaseloadSession) =>
    session.isUpcoming && session.startsOn
      ? `${session.time} (starts ${session.startsOn})`
      : session.time;

  const byDay: { day: string; times: string[] }[] = [];
  sessions.forEach(session => {
    const last = byDay[byDay.length - 1];
    if (last && last.day === session.weekday) last.times.push(label(session));
    else byDay.push({ day: session.weekday, times: [label(session)] });
  });
  return byDay.map(d => `${d.day} ${d.times.join(', ')}`).join('; ');
};

const CaseloadModal = ({
  isOpen,
  onClose,
  providerName,
  caseload
}: IProps) => {
  const rows = useMemo(
    () =>
      caseload.map(client => ({
        ...client,
        dobLabel: formatDob(client.dob),
        sessionsLabel: formatSessions(client.sessions)
      })),
    [caseload]
  );

  return (
    <ModalSemantic
      open={isOpen}
      onClose={onClose}
      modalWidth={620}
      trigger={<div />}
      content={
        <CaseloadContent>
          <h3 className="caseload-title">Unique Clients</h3>
          <p className="caseload-subtitle">
            {caseload.length}{' '}
            {caseload.length === 1 ? 'client' : 'clients'} on{' '}
            {providerName || 'this provider'}
            {"'"}s recurring template, earliest in the week first.
          </p>
          {rows.length === 0 ? (
            <p className="caseload-empty">
              No recurring client sessions on this template.
            </p>
          ) : (
            <table className="caseload-table">
              <thead>
                <tr>
                  <th className="row-number">#</th>
                  <th>Client</th>
                  <th>Days &amp; times</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.clientId}>
                    {/* Numbered so the count never has to be tallied by hand. */}
                    <td className="row-number">{index + 1}</td>
                    <td>
                      <span className="client-name">{row.name}</span>
                      <span className="client-dob">DOB {row.dobLabel}</span>
                    </td>
                    <td>
                      <span className="session-list">{row.sessionsLabel}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="caseload-actions">
            <Button onClick={onClose}>Close</Button>
          </div>
        </CaseloadContent>
      }
    />
  );
};

export default CaseloadModal;
